import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";

interface Props {
  open: boolean;
  playerName: string | null;
  onReturnToLobby: () => void;
  /** Host can replace the disconnected seat with a blind bot (Addata). */
  isHost?: boolean;
  onFillWithBot?: () => void;
  /** Host-only: remove the disconnected human (Addata — seat becomes a blind bot). */
  onRemovePlayer?: () => void;
}

const TIMEOUT_SECONDS = 60;

export const WaitingForPlayerModal = ({
  open,
  playerName,
  onReturnToLobby,
  isHost,
  onFillWithBot,
  onRemovePlayer,
}: Props) => {
  const [secondsLeft, setSecondsLeft] = useState(TIMEOUT_SECONDS);

  useEffect(() => {
    if (!open) {
      setSecondsLeft(TIMEOUT_SECONDS);
      return;
    }
    setSecondsLeft(TIMEOUT_SECONDS);
    const interval = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [open]);

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      onClose={() => {}}
      PaperProps={{
        sx: {
          backgroundColor: "rgba(15, 15, 25, 0.97)",
          border: "1px solid rgba(255,255,255,0.15)",
          borderRadius: 3,
          minWidth: 320,
          textAlign: "center",
        },
      }}
    >
      <DialogTitle sx={{ color: "white", fontWeight: "bold", pb: 1 }}>
        Game Paused
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            py: 1,
          }}
        >
          <CircularProgress color="secondary" size={48} />
          <Typography sx={{ color: "rgba(255,255,255,0.85)" }}>
            <strong style={{ color: "white" }}>
              {playerName ?? "A player"}
            </strong>{" "}
            has disconnected.
          </Typography>
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.5)" }}>
            Waiting for them to reconnect before play can continue…
          </Typography>
          {isHost && onFillWithBot && (
            <Button variant="contained" color="warning" onClick={onFillWithBot} sx={{ mt: 0.5 }}>
              Fill seat with bot
            </Button>
          )}
          {isHost && onRemovePlayer && (
            <Button
              variant="outlined"
              color="error"
              onClick={() => {
                if (window.confirm("Remove this player from the match? Their seat will be filled with a blind bot.")) {
                  onRemovePlayer();
                }
              }}
              sx={{ mt: 0.5 }}
            >
              Remove player
            </Button>
          )}
          {secondsLeft > 0 ? (
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.3)" }}>
              Leave option available in {secondsLeft}s
            </Typography>
          ) : (
            <Button
              variant="outlined"
              color="error"
              onClick={onReturnToLobby}
              sx={{ mt: 1 }}
            >
              Return to Lobby
            </Button>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};
